<template>
  <form novalidate @submit.prevent="onSubmit">
    <LsModal title="Change Mowing Frequency" track-id="change_frequency_modal">
      <template #body>
        <div class="change-frequency-modal--wrapper">
          <p class="has-text-weight-bold">Need to change how often your lawn is serviced?</p>
          <p class="change-frequency-modal--upper-info-text">
            No problem! Just select your new frequency from the options below and we'll make the
            change.
          </p>
        </div>

        <LsInfoCard>
          <p class="is-italic">
            If your grass is growing faster than what is expected by the selected frequency, you may
            be charged a long grass fee to compensate your crew for the extra time taken to service
            your lawn.
          </p>
        </LsInfoCard>
        <div v-if="!properties.loadingStatus.isLoading">
          <div class="change-frequency-modal--wrapper">
            <LsSelect
              v-model="selectedProperty"
              test-id="properties"
              label="Property to reschedule:"
              name="properties"
              :options="propertyOptionsArray"
              track-id="change_frequency_modal-properties"
              @input="getCycleOptions"
            />
          </div>
          <div v-if="hasMow" class="change-frequency-modal--middle">
            <LsRadios
              v-model="selectedCycle"
              label="What frequency would you prefer?"
              track-id="change_frequency_modal-new_cycle"
              name="selected_cycle"
              :options="cycleOptions"
              @input="getImmediacyOptions"
            />
          </div>
        </div>
        <div v-else class="change-frequency-modal--top-spacer"></div>
        <div class="change-frequency-modal--lower-wrapper">
          <div v-if="isUpdatingLocal" class="change-frequency-modal--lower-spacer">
            <BLoading :active.sync="isUpdatingLocal" :is-full-page="false" />
          </div>

          <div v-else-if="hasMow" class="change-frequency-modal--lower">
            <div class="change-frequency-modal--date">
              <span class="change-frequency-modal--price">
                Price: <span class="has-text-weight-bold">{{ price | currency }}</span> <br />
                <span class="change-frequency-modal--price-details">{{ priceDetailString }}</span>
              </span>

              <div class="change-frequency-modal--next-service">
                <LsIcon type="calendar" test-id="icon" size="md" class="calendar" />

                <span class="change-frequency-modal--next-text" test-id="next_text">
                  Next service:
                  <span class="change-frequency-modal--next-date">{{ next_starts_at | date }}</span>
                </span>
              </div>
            </div>
          </div>
          <span v-else class="change-frequency-modal--no-mow-text">
            <p>There are no active subscriptions for this property.</p>
            <p v-if="selectedProperty.can_self_reactivate && !selectedProperty.is_active">
              To order lawn care,
              <span
                class="change-frequency-modal--reactivate"
                test-id="reactivate"
                @click="goToReactivate"
                >reactivate your service</span
              >
            </p>
          </span>
        </div>
      </template>

      <template #footer>
        <LsButton
          text="update schedule"
          test-id="update_schedule_button"
          track-id="change_frequency_modal-update_schedule"
          type="submit"
          :loading="support.loadingStatus.isUpdating"
          :disabled="isUpdatingLocal || !hasMow"
        />
      </template>
    </LsModal>
  </form>
</template>

<script>
import _ from 'lodash';
import { LsModal, LsInfoCard, LsSelect, LsRadios, LsIcon, LsButton } from '@/components/common';
import { mapState, mapActions, mapGetters } from 'vuex';
import {
  SUPPORT__CHANGE_FREQUENCY,
  PROPERTIES__GET_PROPERTIES_AND_SCHEDULES,
  PROPERTIES__PROPERTIES_IN_ARRAY,
  SCHEDULE_CYCLES__GET_DETAILS,
} from '@/store/types';
import { CYCLE_MONTHLY } from '@/constants/scheduleEvent';
import { lsErrorService, lsFilters } from 'ls-vue-common';
import { lsMarketHelpers } from '@/helpers';

export default {
  name: 'ChangeFrequencyModal',
  components: { LsModal, LsInfoCard, LsSelect, LsRadios, LsIcon, LsButton },
  data() {
    return {
      isUpdatingLocal: true,
      isUpdatingFirstLaunch: true,
      selectedProperty: null,
      cycleDetails: [],
      cycleOptions: [],
      immediacyOptions: [],
      selectedCycle: null,
      localImmediacy: null,
      immediate_next_service_date: '',
      price: null,
      priceDetailString: '',
      hasMow: false,
      next_starts_at: null,
      currentCycle: null,
    };
  },
  computed: {
    ...mapState(['support', 'properties', 'schedules', 'auth', 'scheduleCycles']),
    ...mapGetters([PROPERTIES__PROPERTIES_IN_ARRAY]),

    propertyOptionsArray() {
      return _.map(this[PROPERTIES__PROPERTIES_IN_ARRAY], (property) => {
        return {
          label: property.street,
          value: {
            property_id: property.property_id,
            active_mowing_schedule_id: property.active_mowing_schedule_id,
            options: _.get(property.priced.services, 'mowing.options'),
            can_self_reactivate: property.can_self_reactivate,
            is_active: property.is_active,
            market: _.get(property, 'market'),
          },
        };
      });
    },
  },
  async created() {
    await this[PROPERTIES__GET_PROPERTIES_AND_SCHEDULES]();

    this.selectedProperty = this.propertyOptionsArray[0].value;
    this.hasMow = Boolean(this.selectedProperty.active_mowing_schedule_id);

    const activeSchedule = _.get(
      this.schedules.schedulesById,
      this.selectedProperty.active_mowing_schedule_id
    );

    if (activeSchedule) {
      this.currentCycle = activeSchedule.cycle;
    }

    await this.getCycleDetails();
    this.getCycleOptions();
    this.getImmediacyOptions();
    this.getUpcomingService();
  },
  methods: {
    ...mapActions([
      SUPPORT__CHANGE_FREQUENCY,
      PROPERTIES__GET_PROPERTIES_AND_SCHEDULES,
      SCHEDULE_CYCLES__GET_DETAILS,
    ]),
    getCycleOptions() {
      // Exclude monthly mowing if it's disabled by the market
      const excludeMonthlyMowing =
        lsMarketHelpers.isMonthlyMowingDisabled(this.selectedProperty.market) &&
        this.currentCycle !== CYCLE_MONTHLY;
      const propertyOptions = _.filter(this.selectedProperty.options, (propertyOption) => {
        if (propertyOption.cycle === CYCLE_MONTHLY && excludeMonthlyMowing) {
          return false;
        }

        return true;
      });

      // Map the pricing options to select options
      this.cycleOptions = _.map(propertyOptions, (propertyOption) => {
        const isCurrentCycle = this.currentCycle === propertyOption.cycle;

        return {
          disabled: isCurrentCycle,
          value: propertyOption.cycle,
          label: this.getPropertyOptionLabel(propertyOption, isCurrentCycle),
        };
      });
    },

    getPropertyOptionLabel(propertyOption, isCurrentCycle) {
      let label = lsFilters.frequency({ cycle: propertyOption.cycle });
      if (isCurrentCycle) {
        label = `${label} (current)`;
      }

      return `${label} \u2022 ${lsFilters.currency({
        amount: propertyOption.price,
      })} per service`;
    },

    async getCycleDetails() {
      this.isUpdatingLocal = true;

      try {
        await this[SCHEDULE_CYCLES__GET_DETAILS]({
          customerId: this.auth.user.customer_id,
          propertyId: this.selectedProperty.property_id,
          scheduleId: this.selectedProperty.active_mowing_schedule_id,
        });

        const cycleDetails = this.scheduleCycles.details[
          this.selectedProperty.active_mowing_schedule_id
        ];

        // set the  cycle with highest frequency for default selection
        this.selectedCycle = _.get(cycleDetails, '0.cycle', '');

        // key by the cycle so it's easier to fetch
        this.cycleDetails = _.keyBy(cycleDetails, 'cycle');
      } catch (error) {
        this.hasMow = false;
        lsErrorService.log({ error, showAlert: true });
      } finally {
        this.isUpdatingLocal = false;
        this.isUpdatingFirstLaunch = false;
      }
    },
    getImmediacyOptions() {
      const selectedCycleDetails = this.cycleDetails[this.selectedCycle];
      if (!selectedCycleDetails) {
        return;
      }

      const { immediate_available, immediate_customer_starts_at } = selectedCycleDetails;
      this.immediacyOptions = [
        {
          label: `Immediately${!immediate_available ? ' (n/a)' : ''}`,
          value: true,
          disabled: !immediate_available || !this.hasMow,
        },
        {
          label: 'After next service',
          value: false,
          disabled: false,
        },
      ];
      this.localImmediacy = Boolean(immediate_available);
      this.immediate_next_service_date = immediate_customer_starts_at;

      this.getUpcomingService();
    },
    getUpcomingService() {
      const { localImmediacy, immediate_next_service_date } = this;
      const { schedulesById } = this.schedules;

      const schedule = {
        ...schedulesById[this.selectedProperty.active_mowing_schedule_id],
      };
      if (localImmediacy) {
        schedule.next_starts_at = immediate_next_service_date;
      }

      if (!_.get(this.cycleDetails, this.selectedCycle, false)) {
        return;
      }

      const { price, trust_fee, tax, total } = this.cycleDetails[this.selectedCycle];
      this.price = total;

      this.priceDetailString = `${lsFilters.currency({
        amount: price,
      })} + ${lsFilters.currency({
        amount: trust_fee,
      })} T&S Fee + ${lsFilters.currency({
        amount: tax,
      })} Tax`;

      this.next_starts_at = schedule.next_starts_at;
    },

    goToReactivate() {
      this.$router.push({
        name: 'reactivate',
        params: {
          property_id: this.selectedProperty.property_id,
        },
      });
      this.$parent.close();
    },
    onSubmit() {
      const { property_id, active_mowing_schedule_id } = this.selectedProperty;

      this[SUPPORT__CHANGE_FREQUENCY]({
        property_id,
        active_mowing_schedule_id,
        new_cycle: this.selectedCycle,
        onSuccess: this.$parent.close,
      }).then(() => {
        this.getCycleDetails();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.change-frequency-modal--wrapper {
  padding: 20px 0;
}

.change-frequency-modal--upper-info-text {
  padding-top: 6px;
}

.change-frequency-modal--middle {
  padding: 20px 0;
}

.change-frequency-modal--top-spacer {
  height: 216.5px;
}

.change-frequency-modal--lower-wrapper {
  position: relative;
}

.change-frequency-modal--lower {
  border-top: 1px solid $primary-light;
  padding: 20px 0;
}

.change-frequency-modal--lower-spacer {
  height: 221px;
}

.change-frequency-modal--date {
  height: 90px;
  padding: 10px 20px;
  margin-top: 20px;
  background-color: $primary-light;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
}

.change-frequency-modal--next-service {
  color: $primary;
  display: flex;
  align-items: center;
}

.change-frequency-modal--calendar {
  transform: translateY(-1px);
}

.change-frequency-modal--next-text {
  font-size: $size-4;
  padding-left: 6px;
}

.change-frequency-modal--next-date {
  font-size: $size-4;
  font-weight: bold;
}

.change-frequency-modal--no-mow-text {
  height: 120px;
  padding: 10px 20px;
  background-color: $primary-light;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: $primary;
  font-weight: bold;
}
.change-frequency-modal--reactivate {
  cursor: pointer;
  color: $blue;
}

.change-frequency-modal--price-details {
  font-size: $text-size-small;
}

@media (max-width: $mobile-max-width) {
  .change-frequency-modal--wrapper {
    padding-bottom: 0;
  }

  .change-frequency-modal--upper-info-text {
    padding-bottom: 10px;
  }
}

.themed-app {
  .change-frequency-modal--lower {
    border-color: $theme-color-primary-light;
  }
  .change-frequency-modal--date {
    background-color: $theme-color-primary-light;
  }
  .change-frequency-modal--next-service {
    color: $theme-color-primary;
  }
  .change-frequency-modal--next-text {
    font-size: $theme-txt-body-lg;
  }
  .change-frequency-modal--next-date {
    font-size: $theme-txt-body-lg;
  }
  .change-frequency-modal--no-mow-text {
    background-color: $theme-color-primary-light;
    color: $theme-color-primary;
  }
  .change-frequency-modal--reactivate {
    color: $theme-color-blue;
  }
  .change-frequency-modal--price-details {
    font-size: $text-size-small;
  }
}
</style>
